import React, { useMemo, useState } from 'react';
import { Card, Button, Alert, Form, Modal } from 'react-bootstrap';
import { useDispatch, useSelector } from 'react-redux';
import { useTranslation } from 'react-i18next';
import { FiCheck, FiEye, FiEdit, FiSave, FiX, FiTrash2 } from 'react-icons/fi';
import { toast } from 'react-toastify';
import { saveBillBatch } from './billParsingSlice';
import { LoadingState } from '../../utilities/constants';
import { parseUserInfo } from '../../utilities/common';

const ParsedDataView = ({ parsedData, onCancel, onSuccess }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { orgId } = parseUserInfo(useSelector((state) => state.users.user));
  const { batchSaveStatus, batchSaveResult } = useSelector((state) => state.billParsing);

  const [editingMeters, setEditingMeters] = useState({});
  const [groupedMeters, setGroupedMeters] = useState(null);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [deleteTarget, setDeleteTarget] = useState(null);
  const [showDeleteEntityModal, setShowDeleteEntityModal] = useState(false);
  const [deleteEntityTarget, setDeleteEntityTarget] = useState(null);
  const [savingEntityIndexes, setSavingEntityIndexes] = useState(new Set());

  const billsList = useMemo(() => {
    if (!parsedData) return null;

    const billsArray = Array.isArray(parsedData)
      ? parsedData
      : parsedData.results || parsedData.bills || parsedData.data || [];

    if (!Array.isArray(billsArray) || billsArray.length === 0) return null;

    return billsArray.filter((bill) => bill.parsedData?.metadata);
  }, [parsedData]);

  const groupedByEntity = useMemo(() => {
    if (!billsList) return null;

    const groups = {};

    billsList.forEach((bill) => {
      const { metadata, meters } = bill.parsedData;
      const entityName = metadata?.entityName;

      if (!entityName) return;

      if (!groups[entityName]) {
        groups[entityName] = {
          entityName,
          meters: [],
          seenMeterKeys: new Set()
        };
      }

      if (meters && Array.isArray(meters)) {
        meters.forEach((meter) => {
          const meterWithMetadata = {
            ...meter,
            invoiceNumber: metadata?.billNumber || '—',
            provider: metadata?.provider || '—',
            currency: metadata?.currency || '—',
            fileName: bill.fileName,
            viewUrl: bill.viewUrl,
            viewUrlExpiresAt: bill.viewUrlExpiresAt
          };

          const meterKey = JSON.stringify({
            invoiceNumber: meterWithMetadata.invoiceNumber,
            provider: meterWithMetadata.provider,
            currency: meterWithMetadata.currency,
            meterNumber: meterWithMetadata.meterNumber,
            utility: meterWithMetadata.utility,
            activityType: meterWithMetadata.activityType,
            periodStart: meterWithMetadata.periodStart,
            periodEnd: meterWithMetadata.periodEnd,
            contribution: meterWithMetadata.contribution,
            contributionUnit: meterWithMetadata.contributionUnit,
            cost: meterWithMetadata.cost
          });

          if (!groups[entityName].seenMeterKeys.has(meterKey)) {
            groups[entityName].seenMeterKeys.add(meterKey);
            groups[entityName].meters.push(meterWithMetadata);
          }
        });
      }
    });

    return Object.values(groups).map(({ entityName, meters }) => ({
      entityName,
      meters
    }));
  }, [billsList]);

  React.useEffect(() => {
    if (groupedByEntity) {
      setGroupedMeters(JSON.parse(JSON.stringify(groupedByEntity)));
    }
  }, [groupedByEntity]);

  const displayGroups = groupedMeters || groupedByEntity;

  const errorsByFileName = useMemo(() => {
    if (!batchSaveResult?.summary?.errors) return {};

    const errorMap = {};
    batchSaveResult.summary.errors.forEach((err) => {
      errorMap[err.fileName] = err.error;
    });
    return errorMap;
  }, [batchSaveResult]);

  const handleEdit = (groupIndex, meterIndex, meter) => {
    const key = `${groupIndex}-${meterIndex}`;
    setEditingMeters({ ...editingMeters, [key]: { ...meter } });
  };

  const handleCancelEdit = (meterKey) => {
    const newEditingMeters = { ...editingMeters };
    delete newEditingMeters[meterKey];
    setEditingMeters(newEditingMeters);
  };

  const validateMeter = (meter) => {
    const requiredFields = ['periodStart', 'periodEnd', 'contribution', 'contributionUnit'];

    for (const field of requiredFields) {
      const value = meter[field];
      if (!value || value === '—' || value.toString().trim() === '') {
        toast.error(t('Please complete all required fields'));
        return false;
      }
    }

    if (new Date(meter.periodStart) > new Date(meter.periodEnd)) {
      toast.error(t('Period Start Date must not be after Period End Date'));
      return false;
    }

    const contributionValue = parseFloat(meter.contribution);
    if (isNaN(contributionValue) || contributionValue <= 0) {
      toast.error(t('Contribution must be numeric and greater than 0'));
      return false;
    }

    return true;
  };

  const handleSave = (groupIndex, meterIndex) => {
    const meterKey = `${groupIndex}-${meterIndex}`;
    const editingData = editingMeters[meterKey];

    if (!editingData || !validateMeter(editingData)) {
      return;
    }

    const newGroups = [...groupedMeters];
    newGroups[groupIndex].meters[meterIndex] = { ...editingData };
    setGroupedMeters(newGroups);

    const newEditingMeters = { ...editingMeters };
    delete newEditingMeters[meterKey];
    setEditingMeters(newEditingMeters);
  };

  const handleFieldChange = (meterKey, field, value) => {
    setEditingMeters({
      ...editingMeters,
      [meterKey]: { ...editingMeters[meterKey], [field]: value }
    });
  };

  const handleDeleteClick = (groupIndex, meterIndex) => {
    setDeleteTarget({ groupIndex, meterIndex });
    setShowDeleteModal(true);
  };

  const handleCloseDeleteModal = () => {
    setShowDeleteModal(false);
    setDeleteTarget(null);
  };

  const confirmDelete = () => {
    if (!deleteTarget) return;

    const { groupIndex, meterIndex } = deleteTarget;
    const newGroups = [...groupedMeters];
    newGroups[groupIndex].meters.splice(meterIndex, 1);

    if (newGroups[groupIndex].meters.length === 0) {
      newGroups.splice(groupIndex, 1);
    }

    setGroupedMeters(newGroups);

    if (newGroups.length === 0) {
      toast.info(t('All data has been removed'));
    }

    handleCloseDeleteModal();
  };

  const handleDeleteEntityClick = (groupIndex) => {
    setDeleteEntityTarget(groupIndex);
    setShowDeleteEntityModal(true);
  };

  const handleCloseDeleteEntityModal = () => {
    setShowDeleteEntityModal(false);
    setDeleteEntityTarget(null);
  };

  const confirmDeleteEntity = () => {
    if (deleteEntityTarget === null) return;

    const newGroups = [...groupedMeters];
    newGroups.splice(deleteEntityTarget, 1);
    setGroupedMeters(newGroups);

    if (newGroups.length === 0) {
      toast.info(t('All data has been removed'));
    }

    handleCloseDeleteEntityModal();
  };

  const handleSaveEntity = async (groupIndex, entityName) => {
    if (Object.keys(editingMeters).length > 0) {
      toast.error(t('Please save or cancel all edits before saving'));
      return;
    }

    const entityGroup = groupedMeters[groupIndex];
    if (!entityGroup || entityGroup.meters.length === 0) {
      toast.error(t('No data to save for this entity'));
      return;
    }

    setSavingEntityIndexes(new Set([...savingEntityIndexes, groupIndex]));

    const updatedMeters = {};
    entityGroup.meters.forEach((meter) => {
      if (!updatedMeters[meter.fileName]) {
        updatedMeters[meter.fileName] = [];
      }
      updatedMeters[meter.fileName].push(meter);
    });

    const billsToSave = billsList
      .filter((bill) => updatedMeters[bill.fileName])
      .map((bill) => ({
        tempS3Key: bill.tempS3Key,
        fileName: bill.fileName,
        parsedData: {
          metadata: bill.parsedData.metadata,
          meters: updatedMeters[bill.fileName]
        }
      }));

    if (billsToSave.length === 0) {
      toast.error(t('No valid bills to save'));
      setSavingEntityIndexes((prev) => {
        const next = new Set(prev);
        next.delete(groupIndex);
        return next;
      });
      return;
    }

    const result = await dispatch(saveBillBatch({ orgId, bills: billsToSave }));

    setSavingEntityIndexes((prev) => {
      const next = new Set(prev);
      next.delete(groupIndex);
      return next;
    });

    if (result.meta.requestStatus === 'fulfilled') {
      const failedBills = result.payload?.summary?.failedBills || 0;
      if (failedBills === 0) {
        const newGroups = [...groupedMeters];
        newGroups.splice(groupIndex, 1);
        setGroupedMeters(newGroups);

        if (newGroups.length === 0) {
          onSuccess();
        }
      }
    }
  };

  const isSaving = batchSaveStatus === LoadingState.pending;

  const handleConfirmSave = async () => {
    if (Object.keys(editingMeters).length > 0) {
      toast.error(t('Please save or cancel all edits before submitting'));
      return;
    }

    if (!groupedMeters || groupedMeters.length === 0) {
      toast.error(t('No data to save'));
      return;
    }

    const updatedMeters = {};
    if (groupedMeters) {
      groupedMeters.forEach((group) => {
        group.meters.forEach((meter) => {
          if (!updatedMeters[meter.fileName]) {
            updatedMeters[meter.fileName] = [];
          }
          updatedMeters[meter.fileName].push(meter);
        });
      });
    }

    const billsToSave = billsList
      .map((bill) => {
        const editedMeters = updatedMeters[bill.fileName];
        return {
          tempS3Key: bill.tempS3Key,
          fileName: bill.fileName,
          parsedData: {
            metadata: bill.parsedData.metadata,
            meters: editedMeters || bill.parsedData.meters
          }
        };
      })
      .filter((bill) => bill.parsedData.meters && bill.parsedData.meters.length > 0);

    if (billsToSave.length === 0) {
      toast.error(t('No valid bills to save'));
      return;
    }

    const result = await dispatch(saveBillBatch({ orgId, bills: billsToSave }));

    if (result.meta.requestStatus === 'fulfilled') {
      const failedBills = result.payload?.summary?.failedBills || 0;
      if (failedBills === 0) {
        onSuccess();
      }
    }
  };

  if (!billsList || billsList.length === 0) {
    return <Alert variant="warning">{t('No valid bills found in the uploaded files')}</Alert>;
  }

  if (!groupedByEntity || groupedByEntity.length === 0) {
    return <Alert variant="warning">{t('No valid data found in the uploaded files')}</Alert>;
  }

  if (!displayGroups || displayGroups.length === 0) {
    return <Alert variant="warning">{t('No data to display. All items have been removed.')}</Alert>;
  }

  return (
    <>
      <Card>
        <Card.Body>
          <h5 className="mb-1">{t('Batch Upload Results')}</h5>
          <p className="text-muted mb-4" style={{ fontSize: '13px' }}>
            {t('Review extracted data from uploaded bills')}
          </p>

          {displayGroups.map((entityGroup, groupIndex) => {
            const error = errorsByFileName[entityGroup.meters[0]?.fileName];
            const isEntitySaving = savingEntityIndexes.has(groupIndex);
            return (
              <div key={groupIndex} className="mb-4">
                <div className="d-flex justify-content-between align-items-center mb-3">
                  <h5 className="mb-0">
                    {entityGroup.entityName}
                    {error && <span className="ml-2 badge badge-danger">{t('Error')}</span>}
                  </h5>
                  <div>
                    <Button
                      variant="dark"
                      size="sm"
                      className="mr-2"
                      onClick={() => handleSaveEntity(groupIndex, entityGroup.entityName)}
                      disabled={isEntitySaving}
                    >
                      {isEntitySaving ? (
                        <>
                          <span className="spinner-border spinner-border-sm mr-2" style={{ verticalAlign: 'middle' }} />
                          {t('Saving...')}
                        </>
                      ) : (
                        <>
                          <FiSave className="mr-2" style={{ verticalAlign: 'middle' }} />
                          {t('Save')}
                        </>
                      )}
                    </Button>
                    <Button
                      variant="outline-danger"
                      size="sm"
                      onClick={() => handleDeleteEntityClick(groupIndex)}
                      disabled={isEntitySaving}
                    >
                      <FiTrash2 className="mr-2" style={{ verticalAlign: 'middle' }} />
                      {t('Delete')}
                    </Button>
                  </div>
                </div>

                <div className="mb-2">
                  <strong>{t('Entity Name')}:</strong> {entityGroup.entityName}
                </div>

                <h6 className="mt-3 mb-2">{t('Meters')}</h6>

                {entityGroup.meters && entityGroup.meters.length > 0 ? (
                  <div className="table-responsive">
                    <table className="table table-bordered" style={{ fontSize: '14px' }}>
                      <thead className="thead-light">
                        <tr style={{ whiteSpace: 'nowrap' }}>
                          <th style={{ minWidth: '120px' }}>{t('Invoice Number')}</th>
                          <th style={{ minWidth: '120px' }}>{t('Provider')}</th>
                          <th style={{ minWidth: '80px' }}>{t('Currency')}</th>
                          <th style={{ minWidth: '120px' }}>{t('Meter Number')}</th>
                          <th style={{ minWidth: '100px' }}>{t('Utility')}</th>
                          <th style={{ minWidth: '140px' }}>
                            {t('Period Start Date')}
                            <span className="text-danger">*</span>
                          </th>
                          <th style={{ minWidth: '140px' }}>
                            {t('Period End Date')}
                            <span className="text-danger">*</span>
                          </th>
                          <th style={{ minWidth: '120px' }}>
                            {t('Contribution')}
                            <span className="text-danger">*</span>
                          </th>
                          <th style={{ minWidth: '100px' }}>
                            {t('Unit')}
                            <span className="text-danger">*</span>
                          </th>
                          <th style={{ minWidth: '100px' }}>{t('Cost')}</th>
                          <th style={{ minWidth: '100px' }}>{t('Actions')}</th>
                        </tr>
                      </thead>
                      <tbody>
                        {entityGroup.meters.map((meter, meterIndex) => {
                          const meterKey = `${groupIndex}-${meterIndex}`;
                          const isEditing = meterKey in editingMeters;
                          const displayMeter = isEditing ? editingMeters[meterKey] : meter;

                          return (
                            <tr key={meterIndex} style={{ verticalAlign: 'middle' }}>
                              <td>
                                {isEditing ? (
                                  <Form.Control
                                    size="sm"
                                    type="text"
                                    value={displayMeter.invoiceNumber || ''}
                                    onChange={(e) => handleFieldChange(meterKey, 'invoiceNumber', e.target.value)}
                                  />
                                ) : (
                                  displayMeter.invoiceNumber
                                )}
                              </td>
                              <td>
                                {isEditing ? (
                                  <Form.Control
                                    size="sm"
                                    type="text"
                                    value={displayMeter.provider || ''}
                                    onChange={(e) => handleFieldChange(meterKey, 'provider', e.target.value)}
                                  />
                                ) : (
                                  displayMeter.provider
                                )}
                              </td>
                              <td>
                                {isEditing ? (
                                  <Form.Control
                                    size="sm"
                                    type="text"
                                    value={displayMeter.currency || ''}
                                    onChange={(e) => handleFieldChange(meterKey, 'currency', e.target.value)}
                                  />
                                ) : (
                                  displayMeter.currency
                                )}
                              </td>
                              <td>
                                {isEditing ? (
                                  <Form.Control
                                    size="sm"
                                    type="text"
                                    value={displayMeter.meterNumber || ''}
                                    onChange={(e) => handleFieldChange(meterKey, 'meterNumber', e.target.value)}
                                  />
                                ) : (
                                  displayMeter.meterNumber || '—'
                                )}
                              </td>
                              <td>
                                {isEditing ? (
                                  <Form.Control
                                    size="sm"
                                    type="text"
                                    value={displayMeter.utility || ''}
                                    onChange={(e) => handleFieldChange(meterKey, 'utility', e.target.value)}
                                  />
                                ) : (
                                  displayMeter.utility || '—'
                                )}
                              </td>
                              <td>
                                {isEditing ? (
                                  <Form.Control
                                    size="sm"
                                    type="date"
                                    value={displayMeter.periodStart || ''}
                                    onChange={(e) => handleFieldChange(meterKey, 'periodStart', e.target.value)}
                                  />
                                ) : (
                                  displayMeter.periodStart || '—'
                                )}
                              </td>
                              <td>
                                {isEditing ? (
                                  <Form.Control
                                    size="sm"
                                    type="date"
                                    value={displayMeter.periodEnd || ''}
                                    onChange={(e) => handleFieldChange(meterKey, 'periodEnd', e.target.value)}
                                  />
                                ) : (
                                  displayMeter.periodEnd || '—'
                                )}
                              </td>
                              <td>
                                {isEditing ? (
                                  <Form.Control
                                    size="sm"
                                    type="number"
                                    value={displayMeter.contribution || ''}
                                    onChange={(e) => handleFieldChange(meterKey, 'contribution', e.target.value)}
                                  />
                                ) : (
                                  displayMeter.contribution || '—'
                                )}
                              </td>
                              <td>
                                {isEditing ? (
                                  <Form.Control
                                    size="sm"
                                    type="text"
                                    value={displayMeter.contributionUnit || ''}
                                    onChange={(e) => handleFieldChange(meterKey, 'contributionUnit', e.target.value)}
                                  />
                                ) : (
                                  displayMeter.contributionUnit || '—'
                                )}
                              </td>
                              <td>
                                {isEditing ? (
                                  <Form.Control
                                    size="sm"
                                    type="number"
                                    value={displayMeter.cost || ''}
                                    onChange={(e) => handleFieldChange(meterKey, 'cost', e.target.value)}
                                  />
                                ) : (
                                  displayMeter.cost || '—'
                                )}
                              </td>
                              <td>
                                {isEditing ? (
                                  <div className="d-flex">
                                    <Button
                                      variant="link"
                                      size="sm"
                                      className="p-0 mr-2"
                                      onClick={() => handleSave(groupIndex, meterIndex)}
                                      title={t('Save')}
                                    >
                                      <FiSave size={18} className="text-success" />
                                    </Button>
                                    <Button
                                      variant="link"
                                      size="sm"
                                      className="p-0"
                                      onClick={() => handleCancelEdit(meterKey)}
                                      title={t('Cancel')}
                                    >
                                      <FiX size={18} className="text-danger" />
                                    </Button>
                                  </div>
                                ) : (
                                  <div className="d-flex">
                                    {displayMeter.viewUrl && (
                                      <Button
                                        variant="link"
                                        size="sm"
                                        className="p-0 mr-2"
                                        onClick={() => window.open(displayMeter.viewUrl, '_blank')}
                                        title={t('View Bill')}
                                      >
                                        <FiEye size={18} />
                                      </Button>
                                    )}
                                    <Button
                                      variant="link"
                                      size="sm"
                                      className="p-0 mr-2"
                                      onClick={() => handleEdit(groupIndex, meterIndex, meter)}
                                      title={t('Edit')}
                                    >
                                      <FiEdit size={18} />
                                    </Button>
                                    <Button
                                      variant="link"
                                      size="sm"
                                      className="p-0"
                                      onClick={() => handleDeleteClick(groupIndex, meterIndex)}
                                      title={t('Delete')}
                                    >
                                      <FiTrash2 size={18} className="text-danger" />
                                    </Button>
                                  </div>
                                )}
                              </td>
                            </tr>
                          );
                        })}
                      </tbody>
                    </table>
                  </div>
                ) : (
                  <Alert variant="warning">{t('No meters found')}</Alert>
                )}

                {error && (
                  <Alert variant="danger" className="mt-3">
                    <strong>{t('Validation Error')}:</strong> {error}
                  </Alert>
                )}
              </div>
            );
          })}

          <div className="d-flex justify-content-end mt-4">
            <Button variant="outline-secondary" onClick={onCancel} disabled={isSaving} className="mr-2">
              {t('Cancel')}
            </Button>
            <Button variant="dark" onClick={handleConfirmSave} disabled={billsList.length === 0 || isSaving}>
              {isSaving ? (
                <>
                  <span className="spinner-border spinner-border-sm mr-2" />
                  {t('Saving...')}
                </>
              ) : (
                <>
                  <FiCheck className="mr-2" />
                  {t('Confirm & Save All')}
                </>
              )}
            </Button>
          </div>
        </Card.Body>
      </Card>

      <Modal show={showDeleteModal} onHide={handleCloseDeleteModal}>
        <Modal.Header closeButton>
          <Modal.Title>{t('Confirm Delete')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>{t('Are you sure you want to delete this row? This action cannot be undone.')}</Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseDeleteModal}>
            {t('Cancel')}
          </Button>
          <Button variant="danger" onClick={confirmDelete}>
            {t('Delete')}
          </Button>
        </Modal.Footer>
      </Modal>

      <Modal show={showDeleteEntityModal} onHide={handleCloseDeleteEntityModal}>
        <Modal.Header closeButton>
          <Modal.Title>{t('Confirm Delete')}</Modal.Title>
        </Modal.Header>
        <Modal.Body>
          {t(
            'Are you sure you want to delete this entire entity section? This will remove all meter rows associated with this entity. This action cannot be undone.'
          )}
        </Modal.Body>
        <Modal.Footer>
          <Button variant="secondary" onClick={handleCloseDeleteEntityModal}>
            {t('Cancel')}
          </Button>
          <Button variant="danger" onClick={confirmDeleteEntity}>
            {t('Delete')}
          </Button>
        </Modal.Footer>
      </Modal>
    </>
  );
};

export default ParsedDataView;
